import * as React from 'react';
import { Box, Button, Grid, IconButton, Typography, styled } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { isDefined } from 'lib/typeguards';
import { Option } from 'lib/types';
import { EGAEventName, useGAHelpers } from 'lib/hooks/useGAHelper';
import { PerkDiscountScopeType } from 'components/loyalty/models/PerkModel';
import { AdjustmentSchemeRedemptionFullFormData } from './AdjustmentSchemeRedemptionForm';
import { ExpandedMatchCodesInputModal } from 'components/adjustment-schemes/modals/ExpandedMatchCodesInputModal';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';

const discountRules: Option[] = [
    {
        value: 'ALL',
        label: 'all'
    },
    {
        value: 'CHEAPEST',
        label: 'the cheapest'
    },
    {
        value: 'COSTLIEST',
        label: 'the costliest'
    },
    {
        value: 'ALL_ALLOCATE',
        label: 'the allocated'
    }
];

const StyledSelectFormField = styled(SelectFormField)(() => ({
    width: 'auto'
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main
}));

const StyledMatchCodeBox = styled(Box, {
    shouldForwardProp: propName => propName !== 'noWrap'
})(({ noWrap }: { noWrap?: boolean }) => ({
    flexDirection: 'row',
    display: 'flex',
    flexWrap: noWrap ? 'nowrap' : 'wrap',
    lineHeight: 2,
    alignItems: 'center',
    gap: '8px'
}));

interface AdjustmentSchemeDiscountFormProps {
    discountScopeOptions: Option[];
    currencySymbol?: string;
    posProvider: string;
    showChildItemRule: boolean;
    getChildItemRuleOptions: (posProvider: string, scope?: PerkDiscountScopeType) => Option[];
}

const scopeOptionsLabels: Record<PerkDiscountScopeType, string> = {
    ITEM: 'product',
    CATEGORY: 'category',
    BASKET: 'entire basket',
    ITEM_OR_CATEGORY: 'category and/or product',
    CHILD_ITEM: 'modifier product',
    ITEM_AND_CHILD_ITEM: 'product and modifier product'
};

const matchCodeLabels: Record<PerkDiscountScopeType, string> = {
    ITEM: 'match code or product ID(s)',
    CATEGORY: 'match code or category ID(s)',
    BASKET: 'entire basket',
    ITEM_OR_CATEGORY: 'match code, category ID(s) and/or product ID(s)',
    CHILD_ITEM: 'match code or modifier product ID(s)',
    ITEM_AND_CHILD_ITEM: 'match code or product ID(s) and modifier product ID(s)'
};

export const AdjustmentSchemeDiscountForm: React.FC<AdjustmentSchemeDiscountFormProps> = ({
    discountScopeOptions,
    currencySymbol,
    posProvider,
    showChildItemRule,
    getChildItemRuleOptions
}) => {
    const { values, setFieldValue, errors } = useFormikContext<AdjustmentSchemeRedemptionFullFormData>();
    const { logUserEvent } = useGAHelpers();
    const [selectedMatchCodeGroupIndex, setSelectedMatchCodeGroupIndex] = React.useState<number>(-1);

    const selectedMatchCodeGroup = React.useMemo(() => {
        if (selectedMatchCodeGroupIndex === -1) {
            return undefined;
        }
        return values.discount.groupedScopeMatchCodes[selectedMatchCodeGroupIndex];
    }, [values.discount.groupedScopeMatchCodes, selectedMatchCodeGroupIndex]);

    const handleDiscountScopeSelect = React.useCallback(
        (value: any) => {
            if (value === 'CHILD_ITEM' || value === 'ITEM_AND_CHILD_ITEM') {
                setFieldValue('discount.childItemRule', 'EXCLUDE');
            } else {
                setFieldValue('discount.childItemRule', undefined);
            }
        },
        [setFieldValue]
    );

    const discountRulesPerDiscountType = React.useMemo(
        () =>
            values.discount?.type === 'PERCENTAGE'
                ? discountRules.filter(rule => rule.value !== 'ALL_ALLOCATE')
                : discountRules,
        [values]
    );

    const types: Option[] = React.useMemo(
        () => [
            {
                value: 'VALUE',
                label: currencySymbol || 'Value'
            },
            {
                value: 'PERCENTAGE',
                label: '%'
            },
            {
                value: 'OVERRIDE',
                label: 'price override'
            }
        ],
        [currencySymbol]
    );

    const showDiscountRuleField = React.useMemo(
        () =>
            isDefined(values.discount?.scope) &&
            isDefined(values.discount?.type) &&
            isDefined(values.discount?.value) &&
            values.discount?.scope !== 'BASKET',
        [values.discount?.scope, values.discount?.type, values.discount?.value]
    );

    const showChildItemRuleField = React.useMemo(
        () => isDefined(values.discount?.rule) && values.discount?.scope !== 'BASKET' && showChildItemRule,
        [values.discount?.rule, values.discount?.scope, showChildItemRule]
    );

    const showGroupedScopeMatchCodes = React.useMemo(
        () =>
            values.discount?.scope !== 'BASKET' &&
            (values.discount?.childItemRule || (isDefined(values.discount?.rule) && !showChildItemRule)),
        [values.discount?.scope, values.discount?.childItemRule, values.discount?.rule, showChildItemRule]
    );

    const showMaxCountField = React.useMemo(
        () =>
            isDefined(values.discount?.scope) &&
            values.discount?.scope !== 'BASKET' &&
            isDefined(values.discount?.rule) &&
            (values.discount?.rule === 'CHEAPEST' || values.discount?.rule === 'COSTLIEST') &&
            isDefined(values.discount?.groupedScopeMatchCodes) &&
            values.discount?.groupedScopeMatchCodes?.length > 0 &&
            values?.discount?.groupedScopeMatchCodes[0].length > 0,
        [values.discount?.scope, values.discount?.rule, values.discount?.groupedScopeMatchCodes]
    );

    const showAllowQualifierDiscountField = React.useMemo(() => {
        if (isDefined(values.discount?.scope) && values.discount?.scope === 'BASKET') {
            return true;
        }
        return (
            isDefined(values.discount?.rule) &&
            isDefined(values.discount?.type) &&
            isDefined(values.discount?.value) &&
            isDefined(values.discount?.groupedScopeMatchCodes) &&
            isDefined(values.discount?.groupedScopeMatchCodes[0]) &&
            values.discount?.groupedScopeMatchCodes[0].length > 0
        );
    }, [
        values.discount?.scope,
        values.discount?.type,
        values.discount?.value,
        values.discount?.rule,
        values.discount?.groupedScopeMatchCodes
    ]);

    const handleAddCondition = React.useCallback(
        (push: (value: string) => void) => () => {
            logUserEvent(EGAEventName.ScopeMatchCodeAddConditionClick);
            push('');
        },
        [logUserEvent]
    );

    const handleConfirmMatchCodeEdit = React.useCallback(
        (editedMatchCodes: string) => {
            const currentValues = values.discount.groupedScopeMatchCodes;
            if (currentValues && selectedMatchCodeGroupIndex > -1) {
                currentValues[selectedMatchCodeGroupIndex] = editedMatchCodes;
                setFieldValue('discount.groupedScopeMatchCodes', [...currentValues]);
            }
            setSelectedMatchCodeGroupIndex(-1);
        },
        [setFieldValue, selectedMatchCodeGroupIndex, values.discount.groupedScopeMatchCodes]
    );

    const handleCloseExpandedInput = React.useCallback(() => {
        setSelectedMatchCodeGroupIndex(-1);
    }, []);

    const handleExpandInput = React.useCallback((index: number) => {
        setSelectedMatchCodeGroupIndex(index);
    }, []);

    return (
        <Grid size={12}>
            <Box display="flex" flexDirection="column" marginBottom={1.5}>
                <StyledTitle>2.1 Setting up the discount</StyledTitle>
                <Typography variant="caption">
                    This section controls the discountable items. It determines based on the rules that are
                    supplied to it, what items should be discounted and by how much
                </Typography>
            </Box>
            <Grid container spacing={1.5}>
                <Grid size={12}>
                    <Box display="flex" alignItems="center" columnGap="4px">
                        <Typography variant="body2">This adjustment scheme applies a</Typography>
                        <Box width={values.discount?.type === 'OVERRIDE' ? 130 : 40}>
                            <SelectFormField options={types} name="discount.type" />
                        </Box>
                        {values.discount?.type === 'OVERRIDE' && (
                            <Typography variant="body2">of {currencySymbol ?? ''}</Typography>
                        )}
                        <Box width={44}>
                            <TextFormField name="discount.value" type="number" />
                        </Box>
                        <Typography variant="body2">
                            {values.discount?.type === 'OVERRIDE' ? 'to' : 'discount to'}
                        </Typography>
                        <Box>
                            <StyledSelectFormField
                                options={discountScopeOptions}
                                name="discount.scope"
                                onSelect={handleDiscountScopeSelect}
                            />
                        </Box>
                    </Box>
                </Grid>
                {showDiscountRuleField && (
                    <Grid size={12}>
                        <Box display="flex" alignItems="center" columnGap="4px">
                            <Typography variant="body2">
                                {`${
                                    values.discount.type === 'PERCENTAGE'
                                        ? `${values.discount.value}%`
                                        : `${currencySymbol}${values.discount.value}`
                                }`}{' '}
                                off is redeemed on
                            </Typography>
                            <Box>
                                <StyledSelectFormField
                                    options={discountRulesPerDiscountType}
                                    name="discount.rule"
                                />
                            </Box>
                            <Typography variant="body2">product(s) in the basket</Typography>
                        </Box>
                    </Grid>
                )}
                {showChildItemRuleField && (
                    <Grid size={12}>
                        <Box display="flex" alignItems="center" columnGap="4px">
                            <Typography variant="body2">The cost of modifier products are</Typography>
                            <Box>
                                <StyledSelectFormField
                                    options={getChildItemRuleOptions(posProvider, values.discount?.scope)}
                                    name="discount.childItemRule"
                                    disabled={
                                        values.discount?.scope === 'CHILD_ITEM' ||
                                        values.discount?.scope === 'ITEM_AND_CHILD_ITEM'
                                    }
                                />
                            </Box>
                            <Typography variant="body2">when calculating the discount</Typography>
                        </Box>
                    </Grid>
                )}
                {showGroupedScopeMatchCodes && (
                    <Grid size={12}>
                        <FieldArray name="discount.groupedScopeMatchCodes">
                            {({ remove, push }) => (
                                <StyledMatchCodeBox>
                                    <Typography variant="body2" lineHeight={2}>
                                        Adjustment {matchCodeLabels[values.discount.scope]} are
                                    </Typography>
                                    {values.discount?.groupedScopeMatchCodes?.map((_, index, array) => (
                                        <StyledMatchCodeBox noWrap key={index}>
                                            <TextFormField
                                                name={`discount.groupedScopeMatchCodes.${index}`}
                                                placeholder={'e.g. free drink'}
                                            />
                                            {
                                                <IconButton
                                                    size="small"
                                                    // eslint-disable-next-line react/jsx-no-bind
                                                    onClick={() => handleExpandInput(index)}
                                                >
                                                    <OpenInFullIcon />
                                                </IconButton>
                                            }
                                            {index > 0 && (
                                                // eslint-disable-next-line react/jsx-no-bind
                                                <IconButton size="small" onClick={() => remove(index)}>
                                                    <CloseIcon />
                                                </IconButton>
                                            )}
                                            {index >= 0 && array.length - 1 > index && (
                                                <Typography variant="body2">and</Typography>
                                            )}
                                        </StyledMatchCodeBox>
                                    ))}
                                    {
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleAddCondition(push)}
                                        >
                                            Add another condition
                                        </Button>
                                    }
                                </StyledMatchCodeBox>
                            )}
                        </FieldArray>
                        <Typography variant="body2" fontStyle="italic" lineHeight={2}>
                            If you have entered a match code, make sure you assign the match code to the{' '}
                            {scopeOptionsLabels[values.discount.scope]} in your menu via the menu manager page
                            under the Perks column in the table
                        </Typography>
                    </Grid>
                )}
                {showMaxCountField && (
                    <Grid size={12}>
                        <Box display="flex" alignItems="center" columnGap="4px">
                            <Typography variant="body2">The adjustment scheme can be applied</Typography>
                            <Box width={44}>
                                <TextFormField
                                    name="discount.maxCount"
                                    type="number"
                                    showHelperText={false}
                                />
                            </Box>
                            <Typography variant="body2">
                                time(s) in a single basket for each matching qualifier (if a qualifier has
                                been set)
                            </Typography>
                        </Box>
                        {errors.discount?.maxCount && (
                            <Typography variant="body2" color="error">
                                {errors.discount?.maxCount}
                            </Typography>
                        )}
                    </Grid>
                )}
                {showAllowQualifierDiscountField && (
                    <Grid size={12}>
                        <SwitchFormField
                            name="discount.allowQualifierDiscount"
                            label={<MuiFormLabel>Allow qualifier items to be discounted</MuiFormLabel>}
                        />
                    </Grid>
                )}
            </Grid>
            {selectedMatchCodeGroupIndex > -1 && (
                <ExpandedMatchCodesInputModal
                    matchCodes={selectedMatchCodeGroup}
                    open={selectedMatchCodeGroupIndex > -1}
                    onConfirm={handleConfirmMatchCodeEdit}
                    onClose={handleCloseExpandedInput}
                />
            )}
        </Grid>
    );
};
